import { useCallback, useEffect, useState } from 'react';
import { errorText } from '../api/errorText';
import { useV3Api } from '../api/useV3Api';
import { canPropose, choicesOf, planAllText } from './actionModel';

// The action plans of an account and what can be done with them, shared by the Findings and Actions pages. Every call is the API's own route
// (the buttons are the commands New-cmaV3Action, Approve-cmaV3Action and Remove-cmaV3Action). Nothing here says what runs: the API builds the job, and
// nothing reads a queued job back: queuing it is where a plan ends.
//   load     false: do not read the plans at all (the Findings page only creates them); plans stays empty
export function usePlans(code, { load = true } = {}) {
  const { get, post, del } = useV3Api();
  const [list, setList] = useState({ loading: load, items: [], error: '' });
  const [reloads, setReloads] = useState(0);
  const [busy, setBusy] = useState('');                       // the id of the finding or plan being changed
  const [message, setMessage] = useState({ notice: '', problem: '' });

  useEffect(() => {
    if (!code || !load) return undefined;
    let cancelled = false;
    setList((l) => ({ ...l, loading: true }));
    get('v3/actions', { account: code, state: 'all' })
      .then((body) => { if (!cancelled) setList({ loading: false, items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setList({ loading: false, items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, code, load, reloads]);

  const reload = useCallback(() => setReloads((n) => n + 1), []);
  const clear = useCallback(() => setMessage({ notice: '', problem: '' }), []);

  const run = async (key, call, done) => {
    setBusy(key);
    setMessage({ notice: '', problem: '' });
    try {
      const body = await call();
      setMessage({ notice: done(body), problem: '' });
      reload();
      return body;
    } catch (ex) {
      setMessage({ notice: '', problem: errorText(ex) });
      return null;
    } finally {
      setBusy('');
    }
  };

  return {
    plans: list.items,
    loading: list.loading,
    error: list.error,
    busy,
    message,
    clear,
    reload,
    approve: (plan, kind) => run(plan.id, () => post('v3/actions/approve', { account: code, id: plan.id, kind }),
      (body) => `Queued the ${kind} of ${plan.action} for ${plan.subjectName || plan.subject} on ${plan.device} (job ${(((body || {}).plan || {}).steps || {})[kind] ? body.plan.steps[kind].jobId : ''}).`),
    // The second operation, separate from evaluating: make a plan for every open finding that can have one. Nothing is queued.
    planAll: () => run('plan', () => post('v3/actions/plan', { account: code }), planAllText),
    // Test or apply several plans: one approval each (so one refused plan does not stop the others), one after another.
    approveMany: async (selected, kind) => {
      setBusy('bulk');
      setMessage({ notice: '', problem: '' });
      let queued = 0;
      const refused = [];
      for (const plan of selected) {
        try {
          await post('v3/actions/approve', { account: code, id: plan.id, kind });
          queued += 1;
        } catch (ex) {
          refused.push(`${plan.subjectName || plan.subject} on ${plan.device}: ${errorText(ex)}`);
        }
      }
      setMessage({
        notice: queued ? `Queued the ${kind} for ${queued} plan${queued === 1 ? '' : 's'}.` : '',
        problem: refused.length ? `${refused.length} refused. ${refused.slice(0, 3).join(' ')}${refused.length > 3 ? ' ...' : ''}` : '',
      });
      reload();
      setBusy('');
      return { queued, refused };
    },
    // Create actions for the findings picked, one call each (POST v3/actions, the same as New-cmaV3Action -Finding): the finding's own recommended
    // action. A finding with nothing a script can do is skipped, not refused.
    proposeMany: async (selected) => {
      setBusy('bulk');
      setMessage({ notice: '', problem: '' });
      let made = 0;
      let skipped = 0;
      const refused = [];
      for (const finding of selected) {
        if (!canPropose(finding) || !choicesOf(finding).includes(finding.action)) {
          skipped += 1;
          continue;
        }
        try {
          await post('v3/actions', { account: code, finding: finding.id, action: finding.action });
          made += 1;
        } catch (ex) {
          refused.push(`${finding.subjectName || finding.subject} on ${finding.device}: ${errorText(ex)}`);
        }
      }
      const parts = [`${made} action${made === 1 ? '' : 's'} created`];
      if (skipped) parts.push(`${skipped} skipped (no action a script can run)`);
      setMessage({
        notice: `${parts.join('; ')}. Nothing has been queued.`,
        problem: refused.length ? `${refused.length} refused. ${refused.slice(0, 3).join(' ')}${refused.length > 3 ? ' ...' : ''}` : '',
      });
      reload();
      setBusy('');
      return { made, skipped, refused };
    },
    // Delete the plans picked (DELETE v3/actions, one call each, as Remove-cmaV3Action does).
    removeMany: async (selected) => {
      setBusy('bulk');
      setMessage({ notice: '', problem: '' });
      let removed = 0;
      const refused = [];
      for (const plan of selected) {
        try {
          await del('v3/actions', { account: code, id: plan.id });
          removed += 1;
        } catch (ex) {
          refused.push(`${plan.subjectName || plan.subject} on ${plan.device}: ${errorText(ex)}`);
        }
      }
      setMessage({
        notice: removed ? `Deleted ${removed} plan${removed === 1 ? '' : 's'}.` : '',
        problem: refused.length ? `${refused.length} not deleted. ${refused.slice(0, 3).join(' ')}${refused.length > 3 ? ' ...' : ''}` : '',
      });
      reload();
      setBusy('');
      return { removed, refused };
    },
  };
}
